import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtINR } from "@/lib/leadUtils";

export default function TemplateManager({ categories, onChanged }) {
  const [templates, setTemplates] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", category: "", default_amount: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const refresh = async () => {
    const { data } = await api.get("/expense-templates", { params: { include_inactive: true } });
    setTemplates(data.items || []);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!form.category && categories.length) {
      setForm((f) => ({ ...f, category: categories[0].name }));
    }
  }, [categories, form.category]);

  const create = async () => {
    setError("");
    if (!form.name.trim()) { setError("Name is required."); return; }
    const amt = Number(form.default_amount);
    if (!amt || amt <= 0) { setError("Default amount must be > 0."); return; }
    setSubmitting(true);
    try {
      await api.post("/expense-templates", {
        name: form.name.trim(), category: form.category, default_amount: amt,
      });
      toast.success(`Template ${form.name} created`);
      setForm({ name: "", category: categories[0]?.name || "", default_amount: "" });
      setCreating(false);
      refresh();
      onChanged && onChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not create template");
    } finally {
      setSubmitting(false);
    }
  };

  const patch = async (t, body) => {
    try {
      await api.patch(`/expense-templates/${t.id}`, body);
      toast.success(`${t.name} updated`);
      refresh();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    }
  };

  return (
    <div className="bg-white border border-brand-border p-4 mb-4" data-testid="template-manager">
      <div className="flex items-center justify-between mb-3">
        <div className="kicker">RECURRING TEMPLATES</div>
        {!creating && (
          <button
            onClick={() => setCreating(true)}
            className="px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase border border-brand-border"
            data-testid="template-manager-add"
          >
            + Add template
          </button>
        )}
      </div>

      {creating && (
        <div className="border border-brand-border p-3 mb-3 space-y-2" data-testid="template-form">
          <div className="grid grid-cols-2 gap-2">
            <label>
              <span className="kicker block mb-1">Name</span>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Office Rent"
                className="w-full px-2 py-1.5 text-sm border border-brand-border"
                style={{ borderRadius: 0 }}
                data-testid="template-name"
              />
            </label>
            <label>
              <span className="kicker block mb-1">Category</span>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-brand-border"
                style={{ borderRadius: 0 }}
                data-testid="template-category"
              >
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="kicker block mb-1">Default amount</span>
            <input
              type="number"
              value={form.default_amount}
              onChange={(e) => setForm({ ...form, default_amount: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-brand-border"
              style={{ borderRadius: 0 }}
              data-testid="template-amount"
            />
          </label>
          {error && <p className="text-xs text-rose-700" data-testid="template-error">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setCreating(false); setError(""); }}
              disabled={submitting}
              className="px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase text-brand-muted"
              data-testid="template-cancel"
            >Cancel</button>
            <button
              onClick={create}
              disabled={submitting}
              className="px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase text-white"
              style={{ background: "var(--brand-primary)" }}
              data-testid="template-save"
            >{submitting ? "Saving…" : "Save template"}</button>
          </div>
        </div>
      )}

      {templates.length === 0 ? (
        <p className="text-sm italic text-brand-muted" data-testid="templates-empty">
          No templates yet. Create one to get a monthly confirmation prompt for recurring overheads.
        </p>
      ) : (
        <ul className="divide-y divide-brand-border border border-brand-border" data-testid="templates-list">
          {templates.map((t) => (
            <li key={t.id} className="p-3 flex items-center gap-3" data-testid={`template-${t.id}`}>
              <div className="flex-1">
                {editingId === t.id ? (
                  <InlineEdit template={t} onCancel={() => setEditingId(null)} onSave={async (body) => { await patch(t, body); setEditingId(null); }} />
                ) : (
                  <>
                    <div className="text-sm" style={{
                      color: t.active ? "var(--brand-ink)" : "var(--brand-muted)",
                      textDecoration: t.active ? "none" : "line-through",
                    }}>
                      <span className="font-medium">{t.name}</span>
                      <span className="text-xs text-brand-muted ml-2">{t.category} · {fmtINR(t.default_amount)}</span>
                      {!t.active && (
                        <span className="ml-2 px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase"
                              style={{ background: "var(--brand-paper)", color: "var(--brand-muted)" }}>
                          ARCHIVED
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
              {editingId !== t.id && (
                <div className="flex gap-2 text-[10px]">
                  <button
                    onClick={() => setEditingId(t.id)}
                    className="underline text-brand-muted"
                    data-testid={`template-edit-${t.id}`}
                  >Edit</button>
                  <button
                    onClick={() => patch(t, { active: !t.active })}
                    className="underline text-brand-muted"
                    data-testid={`template-toggle-${t.id}`}
                  >{t.active ? "Archive" : "Restore"}</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function InlineEdit({ template, onCancel, onSave }) {
  const [name, setName] = useState(template.name);
  const [amount, setAmount] = useState(String(template.default_amount));
  return (
    <div className="flex items-center gap-2" data-testid={`template-edit-form-${template.id}`}>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="px-2 py-1 text-sm border border-brand-border flex-1"
        style={{ borderRadius: 0 }}
        data-testid={`template-edit-name-${template.id}`}
      />
      <input
        type="number"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        className="w-32 px-2 py-1 text-sm border border-brand-border"
        style={{ borderRadius: 0 }}
        data-testid={`template-edit-amount-${template.id}`}
      />
      <button
        onClick={() => onSave({ name: name.trim(), default_amount: Number(amount) })}
        className="px-2 py-1 text-[10px] tracking-[0.18em] uppercase text-white"
        style={{ background: "var(--brand-primary)" }}
        data-testid={`template-edit-save-${template.id}`}
      >Save</button>
      <button
        onClick={onCancel}
        className="px-2 py-1 text-[10px] tracking-[0.18em] uppercase text-brand-muted"
        data-testid={`template-edit-cancel-${template.id}`}
      >Cancel</button>
    </div>
  );
}
